import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { RadioBooleanContainer } from '@/components/inputs/RadioContainer'
import { RENTAL_PROPERTY_TYPES } from '@/features/jobs/details/tax-type/tax-prep-organizer/data/rental-property-types'
import type { RentalAccordionViewModel } from './RentalAccordionViewModel'
import { FormFieldInfoPanelLabel } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/FormFieldInfoPanelLabel'
import {
  RentalActivityInfoContent,
  PercentageOfOwnershipInfoContent,
  PropertyTypeInfoContent,
  DisposeOfPropertyInfoContent,
  RentalIncomeTaxTreatmentInfoContent,
  QualifiedJointVentureInfoContent,
  BusinessInterestExpenseLimitInfoContent,
  PaymentsForWorkRentalInfoContent,
  PlacedInServiceInfoContent,
} from '../content'
import {
  FormFieldGroup,
  FormFieldSet,
  FormFieldSetLegend,
} from '@/components/Form'
import { FormContainer } from '@/components/FormContainer'
import { useId } from 'react'
import { AddressFieldSet } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/AddressFieldSet'
import type { MaskBlock } from '@/utils/input-mask-util'
import { FormFieldConditional } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/FormFieldConditional'
import { RENTAL_ACTIVITY_TYPES } from '../../../data/rental-activity-types'
import { PropertyType } from '@taxfyle/txf-api-client'
import { FormFieldRequiredLabel } from '../../../components/FormFieldRequiredLabel'
import { StepCardListItemContainer } from '../../../components/StepCardListItemContainer'

const daysInYearMask: MaskBlock[] = [{ type: 'Digit', min: 1, max: 3 }]
const percentageMask: MaskBlock[] = [{ type: 'Digit', min: 1, max: 3 }]

interface RentalAccordionProps {
  vm: RentalAccordionViewModel
  onRemove: () => void
  testId?: string
}

export const RentalAccordion = observer(function RentalAccordion({
  vm,
  onRemove,
  testId,
}: RentalAccordionProps) {
  const formId = useId()
  return (
    <StepCardListItemContainer
      icon="legend-business"
      title={vm.displayName}
      subtitle={vm.displaySubtitle}
      extras={
        <Button
          iconLeft="delete"
          variant="text-destructive"
          label="Remove"
          onPress={onRemove}
          testId={testId ? `${testId}-remove-button` : undefined}
        />
      }
      isExpanded={vm.isExpanded}
      onExpandedChange={vm.toggleExpanded}
      testId={testId}
    >
      <FormContainer id={formId}>
        <FormFieldGroup stackWhenCompact>
          <SelectContainer
            model={vm}
            field="ownerType"
            label={<FormFieldRequiredLabel label="Owner" />}
            items={vm.owners}
            getKey={({ type }) => type}
            getLabel={({ label }) => label}
            required
            testId={testId ? `${testId}-owner-select` : undefined}
          />
          <SelectContainer
            model={vm}
            field="propertyType"
            label={
              <FormFieldInfoPanelLabel
                label="Property type"
                infoContent={PropertyTypeInfoContent}
                required
                infoTestId={
                  testId ? `${testId}-property-type-info-button` : undefined
                }
              />
            }
            items={RENTAL_PROPERTY_TYPES}
            getKey={([code]) => code}
            getLabel={([, label]) => label}
            required
            testId={testId ? `${testId}-property-type-select` : undefined}
          />
          {vm.propertyType === PropertyType.Other && (
            <TextFieldContainer
              model={vm}
              field="propertyTypeDescription"
              label="Property type description"
              testId={
                testId ? `${testId}-property-type-description-input` : undefined
              }
            />
          )}
        </FormFieldGroup>
        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            model={vm}
            field="propertyName"
            label="Property name"
            placeholder="e.g. My Vacation Home"
            testId={testId ? `${testId}-property-name-input` : undefined}
          />
          <SelectContainer
            model={vm}
            field="treatAs"
            label={
              <FormFieldInfoPanelLabel
                label="Rental activity"
                infoContent={RentalActivityInfoContent}
                infoTestId={
                  testId ? `${testId}-rental-activity-info-button` : undefined
                }
              />
            }
            items={RENTAL_ACTIVITY_TYPES}
            getKey={([code]) => code}
            getLabel={([, label]) => label}
            testId={testId ? `${testId}-rental-activity-select` : undefined}
          />
          <TextFieldContainer
            model={vm}
            field="percentageOfOwnership"
            mask={percentageMask}
            label={
              <FormFieldInfoPanelLabel
                label="Percentage of ownership"
                infoContent={PercentageOfOwnershipInfoContent}
                infoTestId={
                  testId
                    ? `${testId}-percentage-ownership-info-button`
                    : undefined
                }
              />
            }
            placeholder="e.g. 100"
            testId={testId ? `${testId}-percentage-ownership-input` : undefined}
          />
        </FormFieldGroup>
        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            model={vm}
            field="fairRentalDays"
            type="number"
            mask={daysInYearMask}
            label={`How many days was this property rented in ${vm.year}?`}
            hint={`Count only days it was available at a normal market rate.`}
            placeholder="e.g. 10"
            testId={testId ? `${testId}-fair-rental-days-input` : undefined}
          />
          <TextFieldContainer
            model={vm}
            field="personalUseDays"
            type="number"
            mask={daysInYearMask}
            label="How many days did you or your family members use this property?"
            hint={`Include personal stays only, not days used for repairs or tenant work.`}
            placeholder="e.g. 10"
            testId={testId ? `${testId}-personal-used-days-input` : undefined}
          />
          <TextFieldContainer
            model={vm}
            field="percentageOfTenantOccupancy"
            type="number"
            mask={percentageMask}
            label="What percentage of the year was this property occupied by tenants?"
            placeholder="e.g. 75"
            testId={
              testId ? `${testId}-percentage-tenant-occupancy-input` : undefined
            }
          />
        </FormFieldGroup>

        <AddressFieldSet
          model={vm.address}
          legend="Property Address"
          testId={testId}
        />

        <FormFieldSet>
          <FormFieldSetLegend>Additional Questions</FormFieldSetLegend>

          <RadioBooleanContainer
            model={vm}
            field="propertyPlacedInServiceDuringYear"
            label={
              <FormFieldInfoPanelLabel
                label={`Did you place this property in service in ${vm.year}?`}
                infoContent={PlacedInServiceInfoContent}
                infoTestId={
                  testId ? `${testId}-placed-in-service-info-button` : undefined
                }
              />
            }
            orientation="horizontal"
            testId={testId ? `${testId}-placed-in-service` : undefined}
          />
          <RadioBooleanContainer
            model={vm}
            field="propertyWasDisposedOfDuringYear"
            label={
              <FormFieldInfoPanelLabel
                label={`Did you dispose of this property in ${vm.year}?`}
                infoContent={DisposeOfPropertyInfoContent}
                infoTestId={
                  testId ? `${testId}-disposed-property-info-button` : undefined
                }
              />
            }
            orientation="horizontal"
            testId={testId ? `${testId}-disposed-property` : undefined}
          />
          <RadioBooleanContainer
            model={vm}
            field="carryToForm8960"
            label={
              <FormFieldInfoPanelLabel
                label="Is this rental income treated as passive for tax purposes?"
                infoContent={RentalIncomeTaxTreatmentInfoContent}
                infoTestId={
                  testId
                    ? `${testId}-rental-income-passive-info-button`
                    : undefined
                }
              />
            }
            orientation="horizontal"
            testId={testId ? `${testId}-rental-income-passive` : undefined}
          />

          <RadioBooleanContainer
            model={vm}
            field="mainHomeOrSecondHome"
            label="Is this property your main home or second home?"
            orientation="horizontal"
            testId={testId ? `${testId}-main-home-or-second-home` : undefined}
          />
          <RadioBooleanContainer
            model={vm}
            field="qualifiedJointVenture"
            label={
              <FormFieldInfoPanelLabel
                label="Did you qualify for the qualified joint venture for this rental?"
                infoContent={QualifiedJointVentureInfoContent}
                infoTestId={
                  testId
                    ? `${testId}-qualified-joint-venture-info-button`
                    : undefined
                }
              />
            }
            orientation="horizontal"
            testId={testId ? `${testId}-qualified-joint-venture` : undefined}
          />
          <RadioBooleanContainer
            model={vm}
            field="electingOutOfBusinessInterestExpenseLimit"
            label={
              <FormFieldInfoPanelLabel
                label="Did you opt out of the business interest expense limit?"
                infoContent={BusinessInterestExpenseLimitInfoContent}
                infoTestId={
                  testId
                    ? `${testId}-business-interest-expense-info-button`
                    : undefined
                }
              />
            }
            orientation="horizontal"
            testId={testId ? `${testId}-business-interest-expense` : undefined}
          />
          <FormFieldSet>
            <RadioBooleanContainer
              model={vm}
              field="didMakePaymentsRequiring1099"
              hint={`Applies to payments for services or contractor work, which may require a 1099.`}
              label={
                <FormFieldInfoPanelLabel
                  label={`Did you pay anyone for work on this rental in ${vm.year}?`}
                  infoContent={PaymentsForWorkRentalInfoContent}
                  infoTestId={
                    testId
                      ? `${testId}-payments-requiring-1099-info-button`
                      : undefined
                  }
                />
              }
              orientation="horizontal"
              testId={testId ? `${testId}-payments-requiring-1099` : undefined}
            />
            <FormFieldConditional when={vm.didMakePaymentsRequiring1099}>
              <RadioBooleanContainer
                model={vm}
                field="willFileForm1099"
                label="Will you file the required 1099 forms for those payments?"
                orientation="horizontal"
                testId={testId ? `${testId}-file-1099-forms` : undefined}
              />
            </FormFieldConditional>
          </FormFieldSet>
        </FormFieldSet>
      </FormContainer>
    </StepCardListItemContainer>
  )
})
